/**
 * Format Utilities
 * Centralized date, time, and number formatting based on user preferences
 *
 * @author      Aslam Bari
 * @date        Nov, 2024
 * @copyright   www.ibirdsservices.com
 */

import jwt_decode from 'jwt-decode';
import moment from 'moment-timezone';
import { resolveEffectiveExchangeRate } from './reportCurrencyAggregate';

/**
 * Get default date format for a locale
 * @param {string} locale - Locale code (e.g., 'en-US', 'en-IN')
 * @returns {string} Default date format
 */
const getDefaultDateFormat = (locale) => {
  const localeFormats = {
    'en-US': 'MM/DD/YYYY',
    'en-IN': 'DD/MM/YYYY',
    'en-GB': 'DD/MM/YYYY',
    'de-DE': 'DD.MM.YYYY',
    'fr-FR': 'DD/MM/YYYY',
    'ja-JP': 'YYYY/MM/DD',
    'zh-CN': 'YYYY/MM/DD',
    'es-ES': 'DD/MM/YYYY',
    'pt-BR': 'DD/MM/YYYY',
  };

  return localeFormats[locale] || 'DD/MM/YYYY';
};

/**
 * Get default time format for a locale
 * @param {string} locale - Locale code (e.g., 'en-US', 'en-IN')
 * @returns {string} Default time format
 */
const getDefaultTimeFormat = (locale) => {
  const localeFormats = {
    'en-US': 'hh:mm A',      // 12-hour with AM/PM
    'en-IN': 'hh:mm A',      // 12-hour with AM/PM
    'en-GB': 'HH:mm',        // 24-hour
    'de-DE': 'HH:mm',        // 24-hour
    'fr-FR': 'HH:mm',        // 24-hour
    'ja-JP': 'HH:mm',        // 24-hour
    'zh-CN': 'HH:mm',        // 24-hour
    'es-ES': 'HH:mm',        // 24-hour
    'pt-BR': 'HH:mm',        // 24-hour
  };

  return localeFormats[locale] || 'HH:mm';
};

/**
 * Universal display format for date only: e.g. "11 Feb 2026"
 * Used across DetailLayout, ListView, reports, dashboard for consistent display.
 */
export const UNIVERSAL_DATE_DISPLAY_FORMAT = 'D MMM YYYY';

/**
 * Universal display format for datetime: e.g. "11 Feb 2026 09:09 AM"
 * No "at" between date and time; used app-wide for consistent display.
 */
export const UNIVERSAL_DATETIME_DISPLAY_FORMAT = 'D MMM YYYY hh:mm A';

/**
 * Validates and sanitizes currency code
 * @param {string} currencyCode - Currency code to validate
 * @returns {string} Valid currency code
 */
const sanitizeCurrencyCode = (currencyCode) => {
  if (!currencyCode) return 'INR';

  // Debug logging to identify the issue
  console.log('## sanitizeCurrencyCode - currencyCode type:', typeof currencyCode);
  console.log('## sanitizeCurrencyCode - currencyCode value:', currencyCode);
  console.log('## sanitizeCurrencyCode - currencyCode is string?', typeof currencyCode === 'string');

  // Ensure currencyCode is a string before calling string methods
  if (typeof currencyCode !== 'string') {
    console.error('## ERROR: currencyCode is not a string! Type:', typeof currencyCode, 'Value:', currencyCode);
    console.error('## ERROR: currencyCode constructor:', currencyCode?.constructor?.name);
    console.error('## ERROR: currencyCode toString:', String(currencyCode));
    return 'INR';
  }

  // If it looks like a number pattern, return default currency
  if (currencyCode.includes('#') || currencyCode.includes(',') || /^\d/.test(currencyCode)) {
    return 'INR';
  }

  // Validate if it's a proper 3-letter currency code
  if (/^[A-Z]{3}$/.test(currencyCode.toUpperCase())) {
    try {
      // Test if the currency code is valid by trying to create a NumberFormat with it
      new Intl.NumberFormat('en-US', { style: 'currency', currency: currencyCode.toUpperCase() });
      return currencyCode.toUpperCase();
    } catch (e) {
      console.warn(`Invalid currency code: ${currencyCode}, falling back to INR`);
      return 'INR';
    }
  }

  return 'INR';
};

/**
 * Sanitize locale string to ensure it's valid for Intl APIs
 * Converts underscores to hyphens (e.g., en_IN -> en-IN)
 * @param {string} locale - Locale string to sanitize
 * @returns {string} Sanitized locale string
 */
const sanitizeLocale = (locale) => {
  if (!locale || typeof locale !== 'string') {
    return 'en-US';
  }

  // Convert underscores to hyphens (en_IN -> en-IN)
  const sanitized = locale.replace(/_/g, '-');

  // Basic validation - should be in format like 'en-US', 'en-IN', etc.
  if (/^[a-z]{2}-[A-Z]{2}$/i.test(sanitized)) {
    return sanitized;
  }

  // If it doesn't match the pattern, return default
  console.warn(`Invalid locale format: ${locale}, using default en-US`);
  return 'en-US';
};

/**
 * Get user preferences from JWT token stored in session storage
 * @returns {Object} User preferences object
 */
export const getUserPreferences = () => {
  try {
    const token = localStorage.getItem('token');
    if (token) {
      const decoded = jwt_decode(token);
      const rawLocale = decoded.locale || 'en-US';
      const locale = sanitizeLocale(rawLocale);

      return {
        time_zone: decoded.time_zone || 'UTC',
        locale: locale,
        // date_format and time_format are optional - if null, we derive from locale
        date_format: decoded.date_format || getDefaultDateFormat(locale),
        time_format: decoded.time_format || getDefaultTimeFormat(locale)
      };
    }
  } catch (error) {
    console.error('Error getting user preferences:', error);
  }

  // Default preferences if token not available or error occurs
  return {
    time_zone: 'UTC',
    locale: 'en-US',
    date_format: 'DD/MM/YYYY',
    time_format: 'HH:mm'
  };
};

/**
 * Format date using universal display format: "11 Feb 2026"
 * Uses user timezone. Use for all UI display (detail, list, reports, dashboard).
 * @param {Date|string} dateString - Date to format
 * @param {string} customFormat - Optional custom format to override (default: universal format)
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString, customFormat = null) => {
  if (!dateString) return '';

  const prefs = getUserPreferences();
  const format = customFormat || UNIVERSAL_DATE_DISPLAY_FORMAT;

  try {
    return moment(dateString)
      .tz(prefs.time_zone)
      .format(format);
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
};

/**
 * Format datetime using universal display format: "11 Feb 2026 09:09 AM"
 * Uses user timezone; no "at" between date and time. Use for all UI display.
 * @param {Date|string} datetimeString - Datetime to format
 * @param {string} customFormat - Optional custom format to override (default: universal format)
 * @returns {string} Formatted datetime string
 */
export const formatDateTime = (datetimeString, customFormat = null) => {
  if (!datetimeString) return '';

  const prefs = getUserPreferences();
  const format = customFormat || UNIVERSAL_DATETIME_DISPLAY_FORMAT;

  try {
    return moment(datetimeString)
      .tz(prefs.time_zone)
      .format(format);
  } catch (error) {
    console.error('Error formatting datetime:', error);
    return datetimeString;
  }
};

/**
 * Format time only based on user preferences
 * @param {Date|string} timeString - Time to format
 * @param {string} customFormat - Optional custom format to override user preference
 * @returns {string} Formatted time string
 */
export const formatTime = (timeString, customFormat = null) => {
  if (!timeString) return '';

  const prefs = getUserPreferences();
  const format = customFormat || prefs.time_format;

  try {
    // Check if it's a time-only string (HH:MM:SS or HH:MM format)
    const timeOnlyPattern = /^(\d{1,2}):(\d{2})(:(\d{2}))?$/;
    const match = String(timeString).match(timeOnlyPattern);

    if (match) {
      // It's a time-only string, parse it with a reference date
      // Use today's date as reference to create a valid datetime
      const today = moment().tz(prefs.time_zone);
      const timeParts = timeString.split(':');
      const hours = parseInt(timeParts[0], 10);
      const minutes = parseInt(timeParts[1], 10);
      const seconds = timeParts[2] ? parseInt(timeParts[2], 10) : 0;

      const timeMoment = today.clone().hour(hours).minute(minutes).second(seconds);
      return timeMoment.format(format);
    } else {
      // It's a full datetime string, parse it normally
      return moment(timeString)
        .tz(prefs.time_zone)
        .format(format);
    }
  } catch (error) {
    console.error('Error formatting time:', error);
    // If parsing fails, try to return a formatted version of the time string
    if (String(timeString).includes(':')) {
      const parts = String(timeString).split(':');
      if (parts.length >= 2) {
        // Return HH:MM format
        return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
      }
    }
    return timeString;
  }
};

/**
 * Format number based on user preferences
 * @param {number} number - Number to format
 * @param {Object} options - Additional Intl.NumberFormat options
 * @returns {string} Formatted number string
 */
export const formatNumber = (number, options = {}) => {
  if (number === null || number === undefined || number === '') return '';

  const prefs = getUserPreferences();
  const rawLocale = options.locale || prefs.locale;
  const locale = sanitizeLocale(rawLocale);

  try {
    const numValue = Number(number);
    if (isNaN(numValue)) return number;

    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: options.minimumFractionDigits,
      maximumFractionDigits: options.maximumFractionDigits,
      ...options
    }).format(numValue);
  } catch (error) {
    console.error('Error formatting number:', error);
    return number;
  }
};

/**
 * Get currency symbol for a currency code (e.g. USD -> $, INR -> ₹)
 * @param {string} currencyCode - Currency code (e.g., 'USD', 'INR', 'AED')
 * @returns {string} Currency symbol
 */
export const getCurrencySymbol = (currencyCode = 'INR') => {
  const code = sanitizeCurrencyCode(currencyCode);
  try {
    const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: code });
    const parts = formatter.formatToParts(0);
    const symbolPart = parts.find((p) => p.type === 'currency');
    return symbolPart ? symbolPart.value : code;
  } catch {
    return code;
  }
};

/**
 * Format currency display for labels (e.g. "USD ($)" or "INR (₹)")
 * @param {string} currencyCode - Currency code
 * @returns {string} "CODE (symbol)"
 */
export const formatCurrencyLabel = (currencyCode = 'INR') => {
  if (!currencyCode) return '-';
  const code = sanitizeCurrencyCode(currencyCode);
  const symbol = getCurrencySymbol(code);
  return `${code} (${symbol})`;
};

/**
 * Format currency based on user preferences
 * @param {number} amount - Amount to format
 * @param {string} currencyCode - Currency code (e.g., 'USD', 'INR', 'EUR')
 * @param {Object} options - Additional Intl.NumberFormat options
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currencyCode = 'INR', options = {}) => {
  if (amount === null || amount === undefined || amount === '') return '';

  const prefs = getUserPreferences();
  const rawLocale = options.locale || prefs.locale;
  const locale = sanitizeLocale(rawLocale);

  const validCurrency = sanitizeCurrencyCode(currencyCode);

  try {
    const numValue = Number(amount);
    if (isNaN(numValue)) return amount;

    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: validCurrency,
      minimumFractionDigits: options.minimumFractionDigits !== undefined ? options.minimumFractionDigits : 2,
      maximumFractionDigits: options.maximumFractionDigits !== undefined ? options.maximumFractionDigits : 2,
      ...options
    });

    // Use formatToParts to get separate parts (currency symbol, number, etc.)
    const parts = formatter.formatToParts(numValue);

    // Reconstruct with space between currency symbol and number
    let result = '';
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      const nextPart = parts[i + 1];
      const prevPart = i > 0 ? parts[i - 1] : null;

      // Check if currency symbol is prefix (before number) or suffix (after number)
      const isPrefix = part.type === 'currency' && nextPart &&
        (nextPart.type === 'integer' || nextPart.type === 'decimal');
      const isSuffix = part.type === 'currency' && prevPart &&
        (prevPart.type === 'decimal' || prevPart.type === 'fraction' || prevPart.type === 'integer');

      // Add space before currency symbol if it's a suffix
      if (isSuffix) {
        result += ' ';
      }

      result += part.value;

      // Add space after currency symbol if it's a prefix
      if (isPrefix) {
        result += ' ';
      }
    }

    return result;
  } catch (error) {
    console.error('Error formatting currency:', error);
    return amount;
  }
};

/**
 * Format percent based on user preferences
 * @param {number} value - Value to format as percentage
 * @param {Object} options - Additional Intl.NumberFormat options
 * @returns {string} Formatted percentage string
 */
export const formatPercent = (value, options = {}) => {
  if (value === null || value === undefined || value === '') return '';

  const prefs = getUserPreferences();
  const rawLocale = options.locale || prefs.locale;
  const locale = sanitizeLocale(rawLocale);

  try {
    const numValue = Number(value);
    if (isNaN(numValue)) return value;

    return new Intl.NumberFormat(locale, {
      style: 'percent',
      minimumFractionDigits: options.minimumFractionDigits !== undefined ? options.minimumFractionDigits : 2,
      maximumFractionDigits: options.maximumFractionDigits !== undefined ? options.maximumFractionDigits : 2,
      ...options
    }).format(numValue / 100);
  } catch (error) {
    console.error('Error formatting percent:', error);
    return value;
  }
};

/**
 * Format a percent field value using field scale (decimal places).
 * Stored values are whole numbers (e.g. 23 = 23%, not 0.23).
 */
export const formatPercentFieldValue = (value, field = {}) => {
  if (value === null || value === undefined || value === '') return '—';
  const numValue = Number(value);
  if (!Number.isFinite(numValue)) return String(value);
  const decimalPlaces =
    field.scale !== undefined && field.scale !== null && field.scale !== ''
      ? parseInt(field.scale, 10)
      : 2;
  return formatPercent(numValue, {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  });
};

const ensurePlainObject = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }
  return { ...value };
};

export const normalizeCalculatedDisplayConfig = (displayConfig) => {
  let rootConfig = ensurePlainObject(displayConfig);

  if (typeof displayConfig === 'string') {
    try {
      const parsed = JSON.parse(displayConfig);
      rootConfig = ensurePlainObject(parsed);
    } catch (error) {
      console.warn('Failed to parse calculated display_config JSON:', error);
      rootConfig = {};
    }
  }

  const calculatedRaw = ensurePlainObject(rootConfig.calculated);

  const pickConfigString = (...candidates) => {
    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim() !== '') {
        return candidate.trim();
      }
    }
    return '';
  };

  const returnTypeRaw = pickConfigString(
    calculatedRaw.returnType,
    calculatedRaw.return_type,
    rootConfig.returnType,
    rootConfig.return_type
  );
  const returnType = returnTypeRaw ? returnTypeRaw.toLowerCase() : 'text';

  const numericDefaultScale = returnType === 'currency' ? 2 : returnType === 'percent' ? 2 : 0;
  const rawScale =
    calculatedRaw.scale ??
    calculatedRaw.decimalPlaces ??
    rootConfig.scale ??
    rootConfig.decimalPlaces;
  let decimalPlaces = Number.isFinite(Number(rawScale)) ? parseInt(rawScale, 10) : null;
  if (decimalPlaces !== null) {
    if (decimalPlaces < 0) decimalPlaces = 0;
    if (decimalPlaces > 6) decimalPlaces = 6;
  } else if (['number', 'currency', 'percent'].includes(returnType)) {
    decimalPlaces = numericDefaultScale;
  }

  const rawPrecision = calculatedRaw.precision ?? rootConfig.precision;
  let precision = Number.isFinite(Number(rawPrecision)) ? parseInt(rawPrecision, 10) : null;
  if (precision !== null) {
    if (precision > 38) precision = 38;
    if (decimalPlaces !== null && precision <= decimalPlaces) {
      precision = Math.min(38, decimalPlaces + 1);
    }
  } else if (['number', 'currency', 'percent'].includes(returnType)) {
    precision = Math.max((decimalPlaces ?? 0) + 10, 18);
    if (precision > 38) precision = 38;
  }

  const currencySymbol =
    returnType === 'currency'
      ? pickConfigString(
          calculatedRaw.currencySymbol,
          calculatedRaw.currency_symbol,
          rootConfig.currencySymbol,
          rootConfig.currency_symbol
        )
      : '';
  const formatPattern = pickConfigString(
    calculatedRaw.formatPattern,
    calculatedRaw.format_pattern,
    rootConfig.formatPattern,
    rootConfig.format_pattern
  );

  return {
    returnType,
    decimalPlaces,
    precision,
    currencySymbol,
    formatPattern,
    rawConfig: rootConfig,
    rawCalculated: calculatedRaw
  };
};

/**
 * Currency from JWT (user currency, else company currency).
 */
export const resolveTokenCurrency = (fallbackCurrency = 'INR') => {
  try {
    const token = localStorage.getItem('token');
    if (token) {
      const decoded = jwt_decode(token);
      const tokenCurrency = decoded.currency;
      if (tokenCurrency && String(tokenCurrency).trim()) {
        return String(tokenCurrency).trim().toUpperCase();
      }
    }
  } catch (error) {
    console.error('Error decoding token for currency:', error);
  }
  return fallbackCurrency;
};

/**
 * Read ISO currency code from a list/detail row or report row.
 * @param {Object|null} record
 * @param {{ fallbackCurrencyCode?: string, objectName?: string }} [options]
 */
export const getCurrencyCodeFromRecord = (record, options = {}) => {
  const fallback =
    options.fallbackCurrencyCode || resolveTokenCurrency('INR');
  if (!record || typeof record !== 'object') {
    return fallback;
  }

  const objectName = options.objectName
    ? String(options.objectName).trim()
    : '';
  if (objectName) {
    const prefixedKey = `${objectName}_currency`;
    if (record[prefixedKey] != null) {
      const prefixed = toDisplayPrimitive(record[prefixedKey]);
      if (prefixed != null && String(prefixed).trim() !== '') {
        return String(prefixed).trim().toUpperCase();
      }
    }
  }

  if (record.currency != null) {
    const fromCurrencyField = toDisplayPrimitive(record.currency);
    if (fromCurrencyField != null && String(fromCurrencyField).trim() !== '') {
      return String(fromCurrencyField).trim().toUpperCase();
    }
  }

  if (Array.isArray(record.fields)) {
    const currencyField = record.fields.find(
      f =>
        String(f?.fieldName || f?.field_name || '').toLowerCase() === 'currency'
    );
    if (currencyField) {
      const fromFields = toDisplayPrimitive(
        currencyField.value ?? currencyField.displayValue
      );
      if (fromFields != null && String(fromFields).trim() !== '') {
        return String(fromFields).trim().toUpperCase();
      }
    }
  }

  for (const key of Object.keys(record)) {
    if (!key.endsWith('_currency') && key !== 'currency') continue;
    const v = toDisplayPrimitive(record[key]);
    if (v != null && String(v).trim() !== '') {
      return String(v).trim().toUpperCase();
    }
  }

  return fallback;
};

/**
 * Options for formatCalculatedValue (record currency fallback for currency return type).
 */
export const getCalculatedFormatOptions = (displayConfig, record = null, options = {}) => {
  const normalized = normalizeCalculatedDisplayConfig(displayConfig);
  if (normalized.returnType !== 'currency') {
    return {};
  }
  return {
    fallbackCurrencyCode: getCurrencyCodeFromRecord(record, {
      objectName: options.objectName,
      fallbackCurrencyCode: options.fallbackCurrencyCode,
    }),
  };
};

/**
 * Format a calculated field object (or value + displayConfig) for UI display.
 * @param {Object|*} fieldOrValue - Field `{ value, displayConfig }` or raw value
 * @param {Object|null} [record] - Row/record for currency fallback
 * @param {{ displayConfig?: *, objectName?: string }} [options]
 */
export const formatCalculatedFieldForDisplay = (fieldOrValue, record = null, options = {}) => {
  const isFieldObject =
    fieldOrValue &&
    typeof fieldOrValue === 'object' &&
    !Array.isArray(fieldOrValue) &&
    (fieldOrValue.displayConfig != null ||
      fieldOrValue.display_config != null ||
      fieldOrValue.value !== undefined);

  const displayConfig = isFieldObject
    ? fieldOrValue.displayConfig ?? fieldOrValue.display_config ?? options.displayConfig
    : options.displayConfig;
  const value = isFieldObject ? fieldOrValue.value : fieldOrValue;
  const row = record ?? (isFieldObject ? fieldOrValue._record : null);

  return formatCalculatedValue(
    value,
    displayConfig,
    getCalculatedFormatOptions(displayConfig, row, {
      objectName: options.objectName,
      fallbackCurrencyCode: options.fallbackCurrencyCode,
    })
  );
};

/**
 * Extract a displayable primitive from a value that may be an object (e.g. API returns { days: 5 }).
 * Prevents "[object Object]" when rendering calculated fields like "Days Since Last Updated".
 * @param {*} value
 * @returns {string|number|boolean|null|undefined}
 */
export const toDisplayPrimitive = (value) => {
  if (value === null || value === undefined) {
    return value;
  }
  if (typeof value !== 'object' || Array.isArray(value)) {
    return value;
  }
  const obj = value;
  if (obj.value !== undefined) {
    const v = toDisplayPrimitive(obj.value);
    if (v !== null && v !== undefined) return v;
  }
  if (obj.displayValue !== undefined) {
    const v = toDisplayPrimitive(obj.displayValue);
    if (v !== null && v !== undefined) return v;
  }
  const preferKeys = ['days', 'number', 'amount', 'count', 'result', 'text', 'label'];
  for (const k of preferKeys) {
    if (Object.prototype.hasOwnProperty.call(obj, k)) {
      const v = obj[k];
      if (v !== null && v !== undefined && (typeof v !== 'object' || v instanceof Date)) return v;
    }
  }
  const firstPrimitive = Object.values(obj).find(
    (v) => v !== null && v !== undefined && (typeof v !== 'object' || v instanceof Date)
  );
  return firstPrimitive !== undefined ? firstPrimitive : null;
};

/**
 * @param {*} value
 * @param {*} displayConfig
 * @param {{ fallbackCurrencyCode?: string }} [options] - Used when returnType is currency and no symbol in config
 */
export const formatCalculatedValue = (value, displayConfig, options = {}) => {
  const primitive = toDisplayPrimitive(value);
  if (primitive === null || primitive === undefined || primitive === '') {
    return '—';
  }
  value = primitive;

  const normalized = normalizeCalculatedDisplayConfig(displayConfig);
  const { returnType, decimalPlaces, currencySymbol, formatPattern } = normalized;
  const fallbackCurrencyCode =
    typeof options.fallbackCurrencyCode === 'string'
      ? options.fallbackCurrencyCode.trim()
      : '';

  switch (returnType) {
    case 'number': {
      return formatNumber(value, {
        minimumFractionDigits: decimalPlaces ?? undefined,
        maximumFractionDigits: decimalPlaces ?? undefined
      });
    }
    case 'currency': {
      const decimals = decimalPlaces ?? 2;
      const numValue = Number(value);
      if (Number.isNaN(numValue)) {
        return String(value);
      }
      const formatOpts = {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      };
      const isoCurrency =
        currencySymbol && /^[A-Z]{3}$/i.test(currencySymbol)
          ? currencySymbol.toUpperCase()
          : '';
      if (isoCurrency) {
        return formatCurrency(numValue, isoCurrency, formatOpts);
      }

      const formattedNumber = formatNumber(numValue, formatOpts);
      if (currencySymbol) {
        const needsSpace = /^[A-Z]{3}$/i.test(currencySymbol) === false &&
          !/[\s$€£¥₹]/.test(currencySymbol.slice(-1));
        return needsSpace
          ? `${currencySymbol} ${formattedNumber}`
          : `${currencySymbol}${formattedNumber}`;
      }
      if (fallbackCurrencyCode) {
        return formatCurrency(numValue, fallbackCurrencyCode, formatOpts);
      }
      return formattedNumber;
    }
    case 'percent': {
      const decimals = decimalPlaces ?? 2;
      const formatted = formatNumber(value, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      });
      return `${formatted}%`;
    }
    case 'checkbox':
    case 'boolean': {
      const boolValue = value === true || value === 'true' || value === 1 || value === '1';
      return boolValue ? 'Yes' : 'No';
    }
    case 'date':
      return formatDate(value);
    case 'datetime':
      return formatDateTime(value);
    case 'url':
      return String(value);
    case 'image':
    case 'barcode':
    case 'qrcode':
      return String(value);
    case 'text':
    default: {
      const stringValue = String(value);
      if (formatPattern && formatPattern.includes('{value}')) {
        try {
          return formatPattern.replace(/\{value\}/g, stringValue);
        } catch (error) {
          console.warn('Failed to apply calculated format pattern:', error);
        }
      }
      return stringValue;
    }
  }
};

/**
 * Convert date from user timezone to UTC for API calls
 * @param {Date|string} dateString - Date in user timezone
 * @returns {string} ISO string in UTC
 */
export const toUTC = (dateString) => {
  if (!dateString) return null;

  const prefs = getUserPreferences();

  try {
    return moment.tz(dateString, prefs.time_zone).utc().toISOString();
  } catch (error) {
    console.error('Error converting to UTC:', error);
    return dateString;
  }
};

/**
 * Convert date from UTC to user timezone
 * @param {string} utcString - UTC datetime string
 * @returns {string} Datetime string in user timezone
 */
export const fromUTC = (utcString) => {
  if (!utcString) return null;

  const prefs = getUserPreferences();

  try {
    return moment.utc(utcString).tz(prefs.time_zone).format();
  } catch (error) {
    console.error('Error converting from UTC:', error);
    return utcString;
  }
};

/**
 * Get current date/time in user's timezone
 * @returns {moment.Moment} Current moment in user timezone
 */
export const now = () => {
  const prefs = getUserPreferences();
  return moment().tz(prefs.time_zone);
};

/**
 * Format field value based on field type and user preferences
 * Generic formatter for use across components
 * @param {Object} field - Field object with type and value
 * @returns {string} Formatted value
 */
/**
 * Exchange rate from a fetchRecords row (supports field-shaped or scalar).
 */
export const getExchangeRateFromRecord = (record) => {
  if (!record || typeof record !== 'object') return null;
  const er = record.exchange_rate ?? record.exchangeRate;
  const primitive = toDisplayPrimitive(er);
  return primitive != null && primitive !== '' ? primitive : null;
};

/**
 * Append base-currency equivalent — same pattern as DetailLayout / ResizableTable.
 */
export const appendBaseCurrencyEquivalentToFormatted = (
  formattedMain,
  numValue,
  record,
  recordCurrency,
  decimalPlaces,
  { baseCurrencyCode, exchangeRateLookup, multiCurrencyEnabled } = {}
) => {
  if (!multiCurrencyEnabled || !baseCurrencyCode || !recordCurrency) {
    return formattedMain;
  }
  if (
    String(recordCurrency).trim().toUpperCase() ===
    String(baseCurrencyCode).trim().toUpperCase()
  ) {
    return formattedMain;
  }
  const rowRate = Number(getExchangeRateFromRecord(record));
  const effectiveRate = resolveEffectiveExchangeRate(
    recordCurrency,
    rowRate,
    exchangeRateLookup
  );
  if (!Number.isFinite(effectiveRate) || effectiveRate <= 0) {
    return formattedMain;
  }
  const opts = {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  };
  const baseEquivalent = numValue / effectiveRate;
  return `${formattedMain} (${formatCurrency(baseEquivalent, baseCurrencyCode, opts)} ${baseCurrencyCode})`;
};

const parseRollupDisplayConfig = (displayConfig) => {
  if (!displayConfig) return {};
  if (typeof displayConfig === 'string') {
    try {
      return JSON.parse(displayConfig);
    } catch {
      return {};
    }
  }
  return displayConfig || {};
};

/**
 * Format a fetchRecords field cell for chatbot/list-style display (currency + base equivalent).
 */
export const formatFetchRecordsFieldForDisplay = (field, record, currencyCtx = {}) => {
  if (!field) return '—';
  const rawValue = field.value;
  if (rawValue === null || rawValue === undefined || rawValue === '') {
    return '—';
  }

  const type = String(field.type || field.data_type || '').toLowerCase();
  const { baseCurrencyCode, exchangeRateLookup, multiCurrencyEnabled, objectName } =
    currencyCtx;

  if (field.displayValue != null && field.displayValue !== '' && type === 'lookup') {
    return String(toDisplayPrimitive(field.displayValue));
  }

  if (type === 'calculated') {
    return formatCalculatedFieldForDisplay(field, record, {
      objectName,
      fallbackCurrencyCode: getCurrencyCodeFromRecord(record, { objectName }),
    });
  }

  const recordCurrency = getCurrencyCodeFromRecord(record, { objectName });

  const formatMoney = (numValue, scale) => {
    const decimalPlaces =
      scale !== undefined && scale !== null && scale !== '' ? parseInt(scale, 10) : 2;
    const opts = {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    };
    const currencyCode = recordCurrency || resolveTokenCurrency('INR');
    let displayValue = formatCurrency(numValue, currencyCode, opts);
    return appendBaseCurrencyEquivalentToFormatted(
      displayValue,
      numValue,
      record,
      recordCurrency,
      decimalPlaces,
      { baseCurrencyCode, exchangeRateLookup, multiCurrencyEnabled }
    );
  };

  if (type === 'currency') {
    const numValue = Number(rawValue);
    if (!Number.isFinite(numValue)) return String(rawValue);
    return formatMoney(numValue, field.scale);
  }

  if (type === 'rollupsummary') {
    const parsed = parseRollupDisplayConfig(field.displayConfig ?? field.display_config);
    const rollupConfig = parsed.rollupSummary || parsed;
    const outputType = String(rollupConfig.outputType || 'number').toLowerCase();
    const numValue = Number(rawValue);
    if (!Number.isFinite(numValue)) return String(rawValue);
    if (outputType === 'currency') {
      return formatMoney(numValue, field.scale ?? rollupConfig.scale);
    }
    if (outputType === 'percent') {
      return formatPercentFieldValue(numValue, field);
    }
  }

  return formatFieldValue({ ...field, _record: record });
};

export const formatFieldValue = (field) => {
  if (!field || field.value === null || field.value === undefined || field.value === '') {
    return '—';
  }

  // Use displayValue for lookup fields if available
  if (field.displayValue) {
    return field.displayValue;
  }

  // Handle different field types
  switch (field.type?.toLowerCase()) {
    case 'date':
      return formatDate(field.value);

    case 'datetime':
      return formatDateTime(field.value);

    case 'time':
      return formatTime(field.value);

    case 'currency':
      return formatCurrency(field.value, field.currencyCode || 'INR', {
        minimumFractionDigits: field.scale !== undefined ? parseInt(field.scale, 10) : 2,
        maximumFractionDigits: field.scale !== undefined ? parseInt(field.scale, 10) : 2
      });

    case 'number':
    case 'integer':
    case 'decimal':
    case 'rollupsummary':
      return formatNumber(field.value, {
        minimumFractionDigits: field.scale !== undefined ? parseInt(field.scale, 10) : undefined,
        maximumFractionDigits: field.scale !== undefined ? parseInt(field.scale, 10) : undefined
      });

    case 'percent':
      return formatPercentFieldValue(field.value, field);

    case 'checkbox':
    case 'boolean':
      const boolValue = field.value;
      if (boolValue === true || boolValue === 'true' || boolValue === 1 || boolValue === '1') {
        return 'Yes';
      } else if (boolValue === false || boolValue === 'false' || boolValue === 0 || boolValue === '0') {
        return 'No';
      }
      return field.value;

    case 'calculated':
      return formatCalculatedFieldForDisplay(field, field._record);

    default:
      return field.value;
  }
};

/**
 * Decode HTML stored with entity encoding (&lt;div&gt;) for detail/email display.
 * Raw HTML (<div>) is returned unchanged.
 */
export function decodeHtmlEntitiesForDisplay(html) {
  if (html == null || html === '') return '';
  const s = String(html);
  if (!/&(?:lt|gt|amp|quot|#39|#x[0-9a-f]+|#\d+);/i.test(s)) {
    return s;
  }
  if (typeof document !== 'undefined') {
    const ta = document.createElement('textarea');
    ta.innerHTML = s;
    return ta.value;
  }
  return s
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&amp;/gi, '&');
}
